import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash, Stack } from "@phosphor-icons/react";

const POS_STATUS = {
  planificat: "Planificat",
  in_productie: "În producție",
  finalizat_fabrica: "Finalizat în fabrică",
  livrat: "Livrat pe șantier",
  montat: "Montat",
};
const SUFFIXES = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export const ProjectPositionsPanel = ({ project, reload }) => {
  const { hasRole } = useAuth();
  const canEdit = hasRole("pm", "inginer", "admin", "vanzari", "asistent_vanzari");
  const [list, setList] = useState(() => JSON.parse(JSON.stringify(project.positions || [])));
  const [saving, setSaving] = useState(false);

  const baseCode = (project.code || "HGS").trim();
  const nextSuffix = () => {
    const used = new Set(list.map((p) => (p.suffix || "").toUpperCase()));
    return SUFFIXES.find((s) => !used.has(s)) || "";
  };
  const add = () => {
    const suffix = nextSuffix();
    setList((l) => [...l, { id: crypto.randomUUID(), suffix, name: "", volum_mc: "", diametru: "", inaltime: "", status: "planificat", note: "" }]);
  };
  const set = (i, k, v) => setList((l) => l.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const del = (i) => setList((l) => l.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${project.id}`, { positions: list });
      toast.success("Rezervoare (poziții) salvate");
      reload?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="project-positions-panel">
      <div className="mb-2 flex items-center gap-2">
        <Stack size={20} weight="bold" className="text-primary" />
        <h3 className="font-head text-lg font-bold">Rezervoare / Poziții contract</h3>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Un contract ({baseCode}) poate avea mai multe rezervoare. Fiecare poziție primește un cod ({baseCode}A, {baseCode}B…) și are propria stare de producție/montaj. Contractul și facturarea rămân comune.
      </p>

      {list.length === 0 && <p className="mb-4 text-sm text-muted-foreground" data-testid="positions-empty">Niciun rezervor adăugat. Pentru un contract cu un singur rezervor nu e nevoie de poziții.</p>}

      <div className="space-y-3">
        {list.map((p, i) => (
          <div key={p.id} className="rounded-lg border border-border bg-white/[0.02] p-3" data-testid={`position-row-${i}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="font-mono text-sm font-bold text-primary">{baseCode}{p.suffix}</span>
              {canEdit && <button onClick={() => del(i)} className="text-muted-foreground hover:text-red-400" title="Șterge poziția" data-testid={`position-del-${i}`}><Trash size={15} /></button>}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="grid gap-1.5"><Label className="text-xs">Cod (sufix)</Label><Input value={p.suffix || ""} onChange={(e) => set(i, "suffix", e.target.value.toUpperCase())} disabled={!canEdit} data-testid={`position-suffix-${i}`} /></div>
              <div className="grid gap-1.5"><Label className="text-xs">Denumire</Label><Input value={p.name || ""} onChange={(e) => set(i, "name", e.target.value)} placeholder="ex: Rezervor apă 120mc" disabled={!canEdit} data-testid={`position-name-${i}`} /></div>
              <div className="grid gap-1.5"><Label className="text-xs">Volum (mc)</Label><Input value={p.volum_mc || ""} onChange={(e) => set(i, "volum_mc", e.target.value)} disabled={!canEdit} /></div>
              <div className="grid gap-1.5"><Label className="text-xs">Ø / Înălțime</Label><div className="flex gap-2"><Input value={p.diametru || ""} onChange={(e) => set(i, "diametru", e.target.value)} placeholder="Ø mm" disabled={!canEdit} /><Input value={p.inaltime || ""} onChange={(e) => set(i, "inaltime", e.target.value)} placeholder="H mm" disabled={!canEdit} /></div></div>
              <div className="grid gap-1.5">
                <Label className="text-xs">Stare</Label>
                <Select value={p.status || "planificat"} onValueChange={(v) => set(i, "status", v)} disabled={!canEdit}>
                  <SelectTrigger data-testid={`position-status-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(POS_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5"><Label className="text-xs">Notă</Label><Input value={p.note || ""} onChange={(e) => set(i, "note", e.target.value)} disabled={!canEdit} /></div>
            </div>
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="mt-4 flex gap-2">
          <Button size="sm" variant="outline" onClick={add} data-testid="position-add-btn"><Plus size={14} className="mr-1" /> Adaugă rezervor</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="positions-save-btn">{saving ? "Se salvează…" : "Salvează pozițiile"}</Button>
        </div>
      )}
    </div>
  );
};
